/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx, keyframes } from "@emotion/react";
import React from "react";

const colStyles = [
  { width: "25%" },
  { width: "10%" },
  { width: "10%" },
  { width: "11%" },
  { width: "15%" },
  { width: "17%" },
  { width: "12%" },
];

const skeletonRowIndexes = [0, 1, 2, 3];

const shimmer = keyframes`
  100% {
    transform: translateX(100%);
  }
`;

type EventSalesDetailsTableSkeletonProps = {
  theme?: "light" | "dark";
  columnLabel?: "券種" | "商品";
};

export const EventSalesDetailsTableSkeleton: React.FC<
  EventSalesDetailsTableSkeletonProps
> = ({ theme = "dark", columnLabel = "券種" }) => {
  const isDark = theme === "dark";
  const styles = React.useMemo(() => getStyles(isDark), [isDark]);

  return (
    <div
      css={styles.tablePanel}
      aria-busy="true"
      // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
      aria-label="券種別売上詳細を読み込み中"
    >
      <div css={styles.scrollContainer}>
        <table css={styles.table}>
          <colgroup>
            {colStyles.map((style, index) => (
              <col key={index} style={style} />
            ))}
          </colgroup>
          <thead css={styles.thead}>
            <tr>
              <th css={[styles.headerCell, styles.headerCellLeft]}>
                {columnLabel}
              </th>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <th css={[styles.headerCell, styles.headerCellLeft]}>販売形態</th>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <th css={[styles.headerCell, styles.headerCellRight]}>価格</th>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <th css={[styles.headerCell, styles.headerCellRight]}>販売数</th>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <th css={[styles.headerCell, styles.headerCellRight]}>
                売上 (¥)
              </th>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <th css={[styles.headerCell, styles.headerCellLeft]}>
                売上構成比
              </th>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <th css={[styles.headerCell, styles.headerCellRight]}>
                購入者数
              </th>
            </tr>
          </thead>

          <tbody css={styles.tbody}>
            {skeletonRowIndexes.map((index) => (
              <tr key={index} css={styles.tableRow}>
                <td colSpan={7} css={styles.cellBase}>
                  <span css={[styles.skeletonBar, styles.rowBar]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getStyles = (isDark: boolean) => ({
  tablePanel: css`
    position: relative;
    background: ${isDark ? "#1b2734" : "#ffffff"};
    border: 1px solid ${isDark ? "#2e3d4c" : "#d9e1ea"};
    border-radius: 10px;
    box-shadow: ${isDark
      ? "0 1px 2px rgba(0, 0, 0, 0.22)"
      : "0 1px 3px rgba(16, 32, 52, 0.08), 0 1px 2px rgba(16, 32, 52, 0.05)"};
    overflow: hidden;
  `,
  scrollContainer: css`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  `,
  table: css`
    width: 100%;
    min-width: 860px;
    border-collapse: collapse;
    table-layout: fixed;
  `,
  thead: css`
    background: ${isDark ? "#233140" : "#eef2f7"};
  `,
  tbody: css`
    background: ${isDark ? "#1b2734" : "#ffffff"};
  `,
  headerCell: css`
    padding: 11px 12px;
    color: ${isDark ? "#8b96a2" : "#66737f"};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.4px;
    vertical-align: middle;
    &:first-of-type {
      padding-left: 24px;
    }
    &:last-of-type {
      padding-right: 24px;
    }
  `,
  headerCellLeft: css`
    text-align: left;
  `,
  headerCellRight: css`
    text-align: right;
  `,
  tableRow: css`
    border-top: 1px solid ${isDark ? "#243342" : "#e7edf4"};
  `,
  cellBase: css`
    padding: 14px 24px;
    vertical-align: middle;
  `,
  skeletonBar: css`
    position: relative;
    display: block;
    overflow: hidden;
    background: ${isDark ? "#243342" : "#e2e8f0"};
    border-radius: 4px;
    height: 16px;
    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.6)"} 50%,
        rgba(255, 255, 255, 0) 100%
      );
      animation: ${shimmer} 1400ms ease-in-out infinite;
      transform: translateX(-100%);
    }
    @media (prefers-reduced-motion: reduce) {
      &::after {
        animation: none;
        transform: translateX(0);
      }
    }
  `,
  rowBar: css`
    width: 100%;
  `,
});
